import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle } from "lucide-react";

interface DeactivateUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userDisplayName: string;
  onDeactivated: () => void;
}

export const DeactivateUserDialog = ({
  open,
  onOpenChange,
  userId,
  userDisplayName,
  onDeactivated
}: DeactivateUserDialogProps) => {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleDelete = async () => {
    if (!reason.trim()) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-user", {
        body: { user_id: userId },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({
        title: "User permanently deleted",
        description: `${userDisplayName} has been removed from the system.`,
      });

      onDeactivated();
      onOpenChange(false);
      setReason("");
    } catch (error: any) {
      console.error("Error deleting user:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete user",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Permanently Delete User
          </DialogTitle>
          <DialogDescription>
            This action permanently deletes all associated data and cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm space-y-1">
            <p className="font-medium">Deleting: {userDisplayName}</p>
            <p className="text-muted-foreground">
              This will remove the user from <strong>auth.users</strong>, profiles,
              all roles, memberships, assessment assignments, and related records.
              The user will no longer be able to log in.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason (required for audit)</Label>
            <Textarea
              id="reason"
              placeholder="Explain why this user is being permanently deleted..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="min-h-[80px]"
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={loading || !reason.trim()}
            >
              {loading ? "Deleting..." : "Delete Permanently"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
